import { getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import { messaging } from '../config/firebase.config';
import type { 
  IFirebaseCloudMessagingService, 
  NotificationPermission, 
  FCMTokenRequest, 
  NotificationData 
} from '../interface/IFirebaseCloudMessagingService';
import type { IHttpWrapperService } from '../interface/IHttpWrapperService';
import { container, TYPES } from '../inversify.config';


export class FirebaseCloudMessagingService implements IFirebaseCloudMessagingService {
  private httpService: IHttpWrapperService;
  private vapidKey: string;
  private currentToken: string | null = null;
  private messageUnsubscribe: (() => void) | null = null;

  constructor() {
    this.httpService = container.get<IHttpWrapperService>(TYPES.IHttpWrapperService);
    this.vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';
  }

  /**
   * Initialize the FCM service
   */
  public async initialize(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        return false;
      }

      if (!messaging) {
        return false;
      }

      // Request permission
      const permission = await this.requestPermission();
      
      if (!permission.granted) {
        return false;
      }

      // Get registration token
      const token = await this.getRegistrationToken();
      
      if (token) {
        this.currentToken = token;
        
        // Send token to backend
        await this.sendTokenToServer(token);
        
        // Set up foreground message listener
        this.setupForegroundMessageListener();
        
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Request notification permission from user
   */
  public async requestPermission(): Promise<NotificationPermission> {
    try {
      if (!('Notification' in window)) {
        return { granted: false, denied: true, default: false };
      }

      const permission = await Notification.requestPermission();
      
      return {
        granted: permission === 'granted',
        denied: permission === 'denied',
        default: permission === 'default'
      };
    } catch (error) {
      return { granted: false, denied: true, default: false };
    }
  }

  /**
   * Get FCM registration token
   */
  public async getRegistrationToken(): Promise<string | null> {
    try {
      if (!messaging) {
        return null;
      }

      const currentToken = await getToken(messaging, {
        vapidKey: this.vapidKey
      });
      
      if (currentToken) {
        return currentToken;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Listen for foreground messages
   */
  public onForegroundMessage(callback: (payload: MessagePayload) => void): void {
    if (!messaging) {
      return;
    }

    this.messageUnsubscribe = onMessage(messaging, (payload) => {
      callback(payload);
    });
  }

  /**
   * Send token to backend server
   */
  public async sendTokenToServer(token: string): Promise<void> {
    try {
      const tokenRequest: FCMTokenRequest = {
        token,
        userId: this.getCurrentUserId(),
        deviceType: 'web',
        browserInfo: this.getBrowserInfo()
      };

      const response = await this.httpService.postRequest<void, FCMTokenRequest>(
        'notifications/tokens',
        tokenRequest
      );

      if (response.status === 200 || response.status === 201) {
        // Token sent successfully
      } else {
        throw new Error('Failed to send token to server');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Remove token from backend server
   */
  public async removeTokenFromServer(token: string): Promise<void> {
    try {
      const response = await this.httpService.deleteRequest(
        `notifications/tokens?token=${encodeURIComponent(token)}`
      );

      if (response.status === 200 || response.status === 204) {
        // Token removed successfully
      } else {
        throw new Error('Failed to remove token from server');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if notifications are supported
   */
  public isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Show local notification
   */
  public showNotification(data: NotificationData): void {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(data.title || 'MT Flow Notification', {
        body: data.body || '',
        icon: data.icon || '/favicon.ico',
        badge: data.badge || '/badge-icon.png',
        data: data.data || {},
        requireInteraction: true,
        tag: 'mt-flow-notification'
      });

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
        
        if (data.url) {
          this.handleNotificationClick(data.url);
        }
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      // Error showing notification
    }
  }

  /**
   * Get current permission status
   */
  public getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return { granted: false, denied: true, default: false };
    }

    const permission = Notification.permission;
    return {
      granted: permission === 'granted',
      denied: permission === 'denied',
      default: permission === 'default'
    };
  }

  /**
   * Cleanup and remove listeners
   */
  public cleanup(): void {
    if (this.messageUnsubscribe) {
      this.messageUnsubscribe();
      this.messageUnsubscribe = null;
    }

    if (this.currentToken) {
      this.removeTokenFromServer(this.currentToken).catch(error => {
        // Error removing token during cleanup
      });
      this.currentToken = null;
    }

  }

  /**
   * Set up foreground message listener with default handler
   */
  private setupForegroundMessageListener(): void {
    this.onForegroundMessage((payload) => {
      const { notification, data } = payload;
      
      if (notification) {
        this.showNotification({
          title: notification.title,
          body: notification.body,
          icon: notification.icon,
          data: data,
          url: data?.url
        });
      }
    });
  }

  /**
   * Handle notification click events
   */
  private handleNotificationClick(url: string): void {
    try {
      if (url.startsWith('http')) {
        window.open(url, '_blank');
      } else {
        // Internal navigation
        window.location.href = url;
      }
    } catch (error) {
      // Error handling notification click
    }
  }

  /**
   * Get current user ID from storage or auth
   */
  private getCurrentUserId(): string {
    // Try to get from localStorage first
    const userId = localStorage.getItem('userId') || 
                  sessionStorage.getItem('userId') ||
                  'anonymous';
    return userId;
  }

  /**
   * Get browser information for analytics
   */
  private getBrowserInfo(): string {
    const userAgent = navigator.userAgent;
    const browser = this.detectBrowser(userAgent);
    const version = this.detectBrowserVersion(userAgent);
    return `${browser} ${version}`;
  }

  /**
   * Detect browser type
   */
  private detectBrowser(userAgent: string): string {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Detect browser version
   */
  private detectBrowserVersion(userAgent: string): string {
    const match = userAgent.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/);
    return match ? match[2] : 'Unknown';
  }
}
